import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Select, Option } from "@material-tailwind/react";
import { api } from "../../services/api/api";
import toast from "react-hot-toast";

/**
 * Mapping des codes de langue vers leurs drapeaux emoji
 */
const flagMap: { [key: string]: string } = {
  "fr": "🇫🇷",
  "en": "🇬🇧", 
  "de": "🇩🇪",
  "es": "🇪🇸",
  "it": "🇮🇹",
  "nl": "🇳🇱",
  "nl-BE": "🇧🇪",
  //"nl_BE": "🇧🇪", // Migration
  "ca": "🏴󠁥󠁳󠁣󠁴󠁿",
  "pl": "🇵🇱",
  "ro": "🇷🇴",
  "lb": "🇱🇺",
  "lb-LU": "🇱🇺",
  "lb_LU": "🇱🇺"
};

/**
 * Composant de sélection de langue pour l'utilisateur
 * 
 * Permet à un utilisateur connecté de changer sa langue.
 * La langue sélectionnée est sauvegardée dans le localStorage et le profil utilisateur.
 * 
 * @param props - Les propriétés du composant
 * @param props.user - L'utilisateur connecté (optionnel)
 * @returns Le composant de sélection de langue
 */
export default function SwitchLanguage({ user }: { user?: EasyTech.User }) {
  const { i18n } = useTranslation();
  
  /**
   * État de la langue actuellement sélectionnée
   * Priorité : langue utilisateur > localStorage > langue i18n (fallback 
   */
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (user?.language?.isoCode) {
      return user.language.isoCode;
    }
    const savedLanguage = localStorage.getItem("i18nextLng");
    return savedLanguage || i18n.language;
  });
  
  /**
   * Liste des langues disponibles
   */
  const [languages, setLanguages] = useState<EasyTech.Language[]>([]);
  const [isChanging, setIsChanging] = useState(false);

  /**
   * Charge la liste des langues disponibles depuis l'API
   */
  useEffect(() => {
    api.get("users/languages")
      .then(ret => {
        setLanguages(ret as EasyTech.Language[]);
      })
      .catch(err => {
        console.error("Erreur lors du chargement des langues:", err);
        setLanguages([]);
      });
  }, []);

  /**
   * Synchronise la langue affichée avec la langue de l'utilisateur
   * La langue de l'utilisateur a la priorité
   */
  useEffect(() => {
    if (user?.language?.isoCode) {
      const userLangCode = user.language.isoCode;
      if (userLangCode !== currentLanguage) {
        setCurrentLanguage(userLangCode);
        if (i18n.language !== userLangCode) {
          i18n.changeLanguage(userLangCode);
        }
      }
    }
  }, [user?.language?.isoCode, currentLanguage, i18n]);

  /**
   * Synchronise avec les changements de langue externes (si l'utilisateur n'a pas de langue définie)
   */
  useEffect(() => {
    if (!user?.language?.isoCode && i18n.language !== currentLanguage) {
      setCurrentLanguage(i18n.language);
    }
  }, [i18n.language, currentLanguage, user?.language?.isoCode]);


  const handleLanguageChange = async (value: string | undefined) => {
    if (value && value !== currentLanguage && user?.id && !isChanging) {
      setIsChanging(true);
      
      try {
        const selectedLang = languages.find(lang => lang.isoCode === value);
        
        if (selectedLang) {
          await i18n.changeLanguage(value);
          setCurrentLanguage(value);
          localStorage.setItem("i18nextLng", value);
          
          const updatedUser = {
            ...user,
            language: selectedLang
          };
          
          await api.put(`admin/users/${user.id}`, updatedUser);

          window.location.reload();
          
          toast.success("Language updated successfully");
        }
      } catch (error) {
        console.error("Erreur lors du changement de langue:", error);
        toast.error("Error updating language");
        
        setCurrentLanguage(user?.language?.isoCode || i18n.language);
        if (user?.language?.isoCode) {
          i18n.changeLanguage(user.language.isoCode);
        }
      } finally {
        setIsChanging(false);
      }
    }
  };

  const currentLangLabel = languages.find(lang => lang.isoCode === currentLanguage)?.label || currentLanguage;

  return (
    <div className="w-40">
      <Select
        value={currentLanguage}
        selected={() => (
          <span className="flex items-center gap-2 leading-none">
            <span className="text-base leading-none">{flagMap[currentLanguage] || "🏳️"}</span>
            <span>{currentLangLabel}</span>
          </span>
        )}
        onChange={handleLanguageChange}
        className="bg-white"
        disabled={isChanging}
      >
        {languages.map((lang) => (
          <Option key={lang.isoCode} value={lang.isoCode} className="flex items-center gap-2">
            <span className="text-base leading-none">{flagMap[lang.isoCode] || "🏳️"}</span>
            <span>{lang.label}</span>
          </Option>
        ))}
      </Select>
    </div>
  );
}

